import { NavLink, Link, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, FilePlus2, LogOut, Mail, Receipt, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../../components/ui/switch";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";

function CompleteProfileDialog() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [clinic, setClinic] = useState("");
  const [country, setCountry] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && user.role === "client" && !user.clinic_name && !sessionStorage.getItem("profile-completion-skipped")) {
      setOpen(true);
    }
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/me/profile", { clinic_name: clinic, country });
      setUser(data);
      setOpen(false);
      toast.success("Profile completed — welcome aboard!");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const skip = () => {
    sessionStorage.setItem("profile-completion-skipped", "1");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) skip(); }}>
      <DialogContent data-testid="complete-profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-xl flex items-center gap-2">
            <Building2 className="h-5 w-5 text-sky-600" /> Complete your practice profile
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">One quick step — this keeps your case records and invoices complete.</p>
        <form onSubmit={save} className="space-y-4" data-testid="complete-profile-form">
          <div className="space-y-2">
            <Label htmlFor="cp-clinic">Clinic / practice name</Label>
            <Input id="cp-clinic" data-testid="profile-clinic-input" required value={clinic}
              onChange={(e) => setClinic(e.target.value)} placeholder="e.g. Smile Studio Dental" className="h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-country">Country <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input id="cp-country" data-testid="profile-country-input" value={country}
              onChange={(e) => setCountry(e.target.value)} placeholder="e.g. United States" className="h-11" />
          </div>
          <Button type="submit" disabled={saving || !clinic.trim()} data-testid="profile-save-btn"
            className="w-full rounded-full bg-sky-600 hover:bg-sky-700 font-bold h-11">
            {saving ? "Saving…" : "Save profile"}
          </Button>
          <button type="button" onClick={skip} data-testid="profile-skip-btn"
            className="w-full text-xs font-semibold text-slate-400 hover:text-slate-600">
            Skip for now
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function PortalLayout() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const toggleDigest = async (v) => {
    try {
      const { data } = await api.patch("/me/preferences", { digest_opt_in: v });
      setUser(data);
      toast.success(v ? "Weekly case digest enabled" : "Weekly case digest disabled");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="portal-layout">
      <CompleteProfileDialog />
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="portal-logo">
            <span className="h-7 w-7 rounded-md bg-sky-600 flex items-center justify-center font-display font-black text-white text-xs">OX</span>
            <span className="font-display font-bold tracking-tight">OrthoPlani<span className="text-sky-600">X</span>
              <span className="ml-2 text-[10px] font-body font-semibold uppercase tracking-widest text-slate-400">Client Portal</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-3">
              {user?.picture && (
                <img src={user.picture} alt={user?.name || "Profile"} referrerPolicy="no-referrer"
                  data-testid="portal-user-avatar" className="h-8 w-8 rounded-full border border-slate-200 object-cover" />
              )}
              <div className="text-right">
                <p className="text-sm font-semibold leading-tight" data-testid="portal-user-name">{user?.name}</p>
                <p className="text-xs text-slate-500 leading-tight">{user?.clinic_name || user?.email}</p>
              </div>
            </div>
            <button onClick={handleLogout} data-testid="portal-logout-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-red-600 transition-colors border border-slate-200 rounded-full px-3.5 py-2">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-[220px_1fr] gap-8">
        <aside>
          <nav className="flex lg:flex-col gap-2">
            <NavLink to="/portal" end data-testid="portal-nav-dashboard"
              className={({ isActive }) => `inline-flex items-center gap-2.5 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-sky-600 text-white" : "text-slate-600 hover:bg-white hover:text-slate-900"}`}>
              <LayoutDashboard className="h-4 w-4" /> My Cases
            </NavLink>
            <NavLink to="/portal/new" data-testid="portal-nav-new-case"
              className={({ isActive }) => `inline-flex items-center gap-2.5 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-sky-600 text-white" : "text-slate-600 hover:bg-white hover:text-slate-900"}`}>
              <FilePlus2 className="h-4 w-4" /> Submit a Case
            </NavLink>
            <NavLink to="/portal/statements" data-testid="portal-nav-statements"
              className={({ isActive }) => `inline-flex items-center gap-2.5 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-sky-600 text-white" : "text-slate-600 hover:bg-white hover:text-slate-900"}`}>
              <Receipt className="h-4 w-4" /> Statements
            </NavLink>
          </nav>
          <div className="mt-6 bg-white border border-slate-200 rounded-xl p-4" data-testid="digest-optin-card">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-bold flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-sky-600" /> Weekly digest</p>
                <p className="text-[11px] text-slate-400 mt-1.5 leading-snug">Get an email summary of your active cases every week.</p>
              </div>
              <Switch checked={!!user?.digest_opt_in} onCheckedChange={toggleDigest} data-testid="digest-optin-switch" />
            </div>
          </div>
        </aside>
        <main className="min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
